import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"
import { renderDateInput } from "@/MyCode/renderers/web/components/DateInputRenderer"
import { useDebounce } from "@/MyCode/hooks/useDebounce"

export function RefinanceCalculator() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    // Existing loan details
    const [existingLoanAmount, setExistingLoanAmount] = useState(300000)
    const [existingLoanTerm, setExistingLoanTerm] = useState(30)
    const [loanTakenDate, setLoanTakenDate] = useState(`${currentYear - 5}-${String(currentMonth).padStart(2, '0')}-01`)
    const [existingInterestRate, setExistingInterestRate] = useState(7.0)

    // New loan details
    const [newLoanTerm, setNewLoanTerm] = useState(30)
    const [newInterestRate, setNewInterestRate] = useState(5.0)
    const [points, setPoints] = useState(1.0)
    const [costsAndFees, setCostsAndFees] = useState(2000)
    const [cashOutAmount, setCashOutAmount] = useState(0)

    // Debounced values
    const debouncedExistingLoanAmount = useDebounce(existingLoanAmount, 500)
    const debouncedExistingLoanTerm = useDebounce(existingLoanTerm, 500)
    const debouncedLoanTakenDate = useDebounce(loanTakenDate, 500)
    const debouncedExistingInterestRate = useDebounce(existingInterestRate, 500)
    const debouncedNewLoanTerm = useDebounce(newLoanTerm, 500)
    const debouncedNewInterestRate = useDebounce(newInterestRate, 500)
    const debouncedPoints = useDebounce(points, 500)
    const debouncedCostsAndFees = useDebounce(costsAndFees, 500)
    const debouncedCashOutAmount = useDebounce(cashOutAmount, 500)

    // Calculate months since loan was taken
    const monthsSinceLoanTaken = useMemo(() => {
        if (!debouncedLoanTakenDate) return 0
        const loanDate = new Date(debouncedLoanTakenDate)
        const currentDate = new Date(currentYear, currentMonth - 1, 1)
        const monthsDiff = (currentDate.getFullYear() - loanDate.getFullYear()) * 12 +
            (currentDate.getMonth() - loanDate.getMonth())
        return Math.max(0, monthsDiff)
    }, [debouncedLoanTakenDate, currentYear, currentMonth])

    // Calculate remaining balance on existing loan
    const existingLoanRemaining = useMemo(() => {
        const monthlyRate = debouncedExistingInterestRate / 100 / 12
        const totalPayments = debouncedExistingLoanTerm * 12

        if (totalPayments === 0 || monthsSinceLoanTaken >= totalPayments) {
            return 0
        }

        // Calculate original monthly payment
        const monthlyPayment = monthlyRate === 0
            ? debouncedExistingLoanAmount / totalPayments
            : (debouncedExistingLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)

        // Calculate remaining balance
        let balance = debouncedExistingLoanAmount
        for (let month = 1; month <= monthsSinceLoanTaken && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            const principal = monthlyPayment - interest
            balance -= principal
        }

        return Math.max(0, balance > 0.01 ? balance : 0)
    }, [debouncedExistingLoanAmount, debouncedExistingInterestRate, debouncedExistingLoanTerm, monthsSinceLoanTaken])

    // Calculate remaining months on existing loan
    const remainingMonthsExisting = useMemo(() => {
        return Math.max(0, (debouncedExistingLoanTerm * 12) - monthsSinceLoanTaken)
    }, [debouncedExistingLoanTerm, monthsSinceLoanTaken])

    // Calculate existing loan monthly payment
    const existingMonthlyPayment = useMemo(() => {
        const monthlyRate = debouncedExistingInterestRate / 100 / 12
        const totalPayments = debouncedExistingLoanTerm * 12

        if (totalPayments === 0) return 0

        return monthlyRate === 0
            ? debouncedExistingLoanAmount / totalPayments
            : (debouncedExistingLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)
    }, [debouncedExistingLoanAmount, debouncedExistingInterestRate, debouncedExistingLoanTerm])

    // Calculate new loan amount (remaining balance + cash out)
    const newLoanAmount = useMemo(() => {
        return existingLoanRemaining + debouncedCashOutAmount
    }, [existingLoanRemaining, debouncedCashOutAmount])

    // Calculate points cost
    const pointsCost = useMemo(() => {
        return (newLoanAmount * debouncedPoints) / 100
    }, [newLoanAmount, debouncedPoints])

    // Calculate total upfront cost
    const totalUpfrontCost = useMemo(() => {
        return pointsCost + debouncedCostsAndFees
    }, [pointsCost, debouncedCostsAndFees])

    // Calculate new loan monthly payment (based on full loan amount)
    const newMonthlyPayment = useMemo(() => {
        const monthlyRate = debouncedNewInterestRate / 100 / 12
        const totalPayments = debouncedNewLoanTerm * 12

        if (totalPayments === 0 || newLoanAmount <= 0) return 0

        return monthlyRate === 0
            ? newLoanAmount / totalPayments
            : (newLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)
    }, [newLoanAmount, debouncedNewInterestRate, debouncedNewLoanTerm])

    // Calculate APR (Annual Percentage Rate) - includes points and fees
    // APR is calculated using an iterative method to find the rate that makes the present value of payments equal to the effective loan amount
    const newLoanAPR = useMemo(() => {
        if (newLoanAmount <= 0 || debouncedNewLoanTerm <= 0 || newMonthlyPayment <= 0) return debouncedNewInterestRate

        const totalPayments = debouncedNewLoanTerm * 12

        // Effective loan amount (what borrower actually receives after paying points and fees)
        const effectiveLoanAmount = newLoanAmount - totalUpfrontCost

        if (effectiveLoanAmount <= 0) return debouncedNewInterestRate

        // Use binary search to find APR
        // APR is the rate where PV of all payments equals effective loan amount
        let low = 0
        let high = 0.5 // 50% APR max
        let apr = debouncedNewInterestRate / 100
        const tolerance = 0.0001

        for (let i = 0; i < 100; i++) {
            apr = (low + high) / 2
            const testMonthlyRate = apr / 12

            // Calculate present value of payments at this APR
            const pv = testMonthlyRate === 0
                ? newMonthlyPayment * totalPayments
                : (newMonthlyPayment * (1 - Math.pow(1 + testMonthlyRate, -totalPayments))) / testMonthlyRate

            if (Math.abs(pv - effectiveLoanAmount) < tolerance) {
                break
            }

            if (pv < effectiveLoanAmount) {
                high = apr
            } else {
                low = apr
            }
        }

        return apr * 100
    }, [newLoanAmount, debouncedNewInterestRate, debouncedNewLoanTerm, totalUpfrontCost, newMonthlyPayment])

    // Calculate total interest for existing loan (remaining)
    const existingLoanTotalInterest = useMemo(() => {
        const monthlyRate = debouncedExistingInterestRate / 100 / 12
        const totalPayments = debouncedExistingLoanTerm * 12
        const monthlyPayment = monthlyRate === 0
            ? debouncedExistingLoanAmount / totalPayments
            : (debouncedExistingLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)

        let balance = existingLoanRemaining
        let totalInterest = 0

        for (let month = 1; month <= remainingMonthsExisting && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            const principal = monthlyPayment - interest
            balance -= principal
            totalInterest += interest
        }

        return totalInterest
    }, [debouncedExistingInterestRate, debouncedExistingLoanAmount, debouncedExistingLoanTerm, existingLoanRemaining, remainingMonthsExisting])

    // Calculate total interest for new loan
    const newLoanTotalInterest = useMemo(() => {
        const monthlyRate = debouncedNewInterestRate / 100 / 12
        const totalPayments = debouncedNewLoanTerm * 12

        if (totalPayments === 0 || newLoanAmount <= 0) return 0

        const monthlyPayment = monthlyRate === 0
            ? newLoanAmount / totalPayments
            : (newLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)

        let balance = newLoanAmount
        let totalInterest = 0

        for (let month = 1; month <= totalPayments && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            const principal = monthlyPayment - interest
            balance -= principal
            totalInterest += interest
        }

        return totalInterest
    }, [newLoanAmount, debouncedNewInterestRate, debouncedNewLoanTerm])

    // Calculate monthly savings
    const monthlySavings = useMemo(() => {
        return existingMonthlyPayment - newMonthlyPayment
    }, [existingMonthlyPayment, newMonthlyPayment])

    // Calculate time saved (months faster)
    const monthsFaster = useMemo(() => {
        return remainingMonthsExisting - debouncedNewLoanTerm * 12
    }, [remainingMonthsExisting, debouncedNewLoanTerm])

    // Calculate lifetime savings
    const lifetimeSavings = useMemo(() => {
        const existingTotalPayments = existingMonthlyPayment * remainingMonthsExisting
        const newTotalPayments = newMonthlyPayment * (debouncedNewLoanTerm * 12)
        return (existingTotalPayments - newTotalPayments) - totalUpfrontCost
    }, [existingMonthlyPayment, remainingMonthsExisting, newMonthlyPayment, debouncedNewLoanTerm, totalUpfrontCost])

    // Calculate break-even point (months to recover upfront costs)
    const breakEvenMonths = useMemo(() => {
        if (monthlySavings <= 0) return null
        return Math.ceil(totalUpfrontCost / monthlySavings)
    }, [totalUpfrontCost, monthlySavings])

    // Calculate total monthly payments for existing loan
    const existingTotalMonthlyPayments = useMemo(() => {
        return existingMonthlyPayment * remainingMonthsExisting
    }, [existingMonthlyPayment, remainingMonthsExisting])

    // Calculate total monthly payments for new loan
    const newTotalMonthlyPayments = useMemo(() => {
        return newMonthlyPayment * (debouncedNewLoanTerm * 12)
    }, [newMonthlyPayment, debouncedNewLoanTerm])

    // Determine if refinancing is beneficial
    const isRefinancingBeneficial = useMemo(() => {
        return lifetimeSavings > 0 && monthlySavings > 0
    }, [lifetimeSavings, monthlySavings])

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.existingLoanAmount !== undefined) setExistingLoanAmount(Number(data.existingLoanAmount))
        if (data.existingLoanTerm !== undefined) setExistingLoanTerm(Number(data.existingLoanTerm))
        if (data.loanTakenDate !== undefined) setLoanTakenDate(data.loanTakenDate)
        if (data.existingInterestRate !== undefined) setExistingInterestRate(Number(data.existingInterestRate))
        if (data.newLoanTerm !== undefined) setNewLoanTerm(Number(data.newLoanTerm))
        if (data.newInterestRate !== undefined) setNewInterestRate(Number(data.newInterestRate))
        if (data.points !== undefined) setPoints(Number(data.points))
        if (data.costsAndFees !== undefined) setCostsAndFees(Number(data.costsAndFees))
        if (data.cashOutAmount !== undefined) setCashOutAmount(Number(data.cashOutAmount))
    }

    const steps: Step[] = [
        {
            id: "existing-loan",
            title: "Existing Loan Details",
            description: "Enter your current loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "existingLoanAmount",
                    label: "Existing Loan Amount",
                    placeholder: "300000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        existingLoanAmount: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "existingLoanTerm",
                    label: "Loan Term (years)",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        existingLoanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "dateInput",
                    name: "loanTakenDate",
                    label: "Loan Taken Month and Year",
                    required: true,
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        loanTakenDate: 'required|date'
                    }
                },
                {
                    type: "number",
                    name: "existingInterestRate",
                    label: "Existing Interest Rate (%)",
                    placeholder: "7.0",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    step: 0.01,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        existingInterestRate: 'required|numeric|min:0|max:100'
                    }
                },
            ]
        },
        {
            id: "new-loan",
            title: "New Loan Details",
            description: "Enter the refinance loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "newLoanTerm",
                    label: "New Loan Term for Remaining Amount (years)",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        newLoanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "newInterestRate",
                    label: "New Interest Rate (%)",
                    placeholder: "5.0",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [0.01, 0.1, 1],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        newInterestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "number",
                    name: "points",
                    label: "Points (Discount Points)",
                    placeholder: "1.0",
                    required: false,
                    minValue: 0,
                    maxValue: 10,
                    step: 0.1,
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        points: 'nullable|numeric|min:0|max:10'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "costsAndFees",
                    label: "Costs and Fees",
                    placeholder: "2000",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        costsAndFees: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "cashOutAmount",
                    label: "Cash Out Amount",
                    placeholder: "0",
                    required: false,
                    description: "Additional amount you want to take on top of the remaining loan amount",
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        cashOutAmount: 'nullable|numeric|min:0'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Refinance Calculator</h1>
                <p className="text-muted-foreground">
                    Compare your current loan with a refinance option to see potential savings
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            existingLoanAmount: existingLoanAmount,
                            existingLoanTerm: existingLoanTerm,
                            loanTakenDate: loanTakenDate,
                            existingInterestRate: existingInterestRate,
                            newLoanTerm: newLoanTerm,
                            newInterestRate: newInterestRate,
                            points: points,
                            costsAndFees: costsAndFees,
                            cashOutAmount: cashOutAmount,
                        }}
                        resetOnInitialValuesChange={false}
                        customFieldRenderers={{
                            dateInput: renderDateInput
                        }}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Summary Card */}
                    <div className={`bg-card border-2 rounded-lg p-6 ${isRefinancingBeneficial ? 'border-green-500/20' : 'border-yellow-500/20'}`}>
                        <h2 className="text-xl font-semibold mb-4">Refinance Summary</h2>
                        <div className="space-y-4">
                            {/* Summary Text */}
                            <div className="mb-4 pb-4 border-b border-border">
                                <p className="text-sm leading-relaxed">
                                    The APR for the new loan is <span className="font-bold"><AnimatedNumber value={newLoanAPR} decimals={3} />%</span>, which is{" "}
                                    <span className={`font-bold ${(debouncedExistingInterestRate - newLoanAPR) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        <AnimatedNumber value={Math.abs(debouncedExistingInterestRate - newLoanAPR)} decimals={3} />%
                                    </span>{" "}
                                    {debouncedExistingInterestRate > newLoanAPR ? 'lower' : 'higher'} than the{" "}
                                    <span className="font-bold"><AnimatedNumber value={debouncedExistingInterestRate} decimals={1} />%</span> interest rate of the current loan.{" "}
                                    {isRefinancingBeneficial ? (
                                        <span className="text-green-600 font-semibold">Refinancing would be financially less expensive.</span>
                                    ) : (
                                        <span className="text-yellow-600 font-semibold">Refinancing may not be financially beneficial.</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">APR for New Loan</p>
                                <p className="text-2xl font-bold">
                                    <AnimatedNumber value={newLoanAPR} decimals={3} />%
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">New Monthly Payment</p>
                                <p className="text-2xl font-bold">
                                    $<AnimatedNumber value={newMonthlyPayment} decimals={2} />
                                </p>
                            </div>
                            {monthlySavings > 0 && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Monthly Savings</p>
                                    <p className="text-xl font-bold text-green-600">
                                        $<AnimatedNumber value={monthlySavings} decimals={2} />/month
                                    </p>
                                </div>
                            )}
                            {monthsFaster > 0 && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Time Saved</p>
                                    <p className="text-xl font-bold text-green-600">
                                        {monthsFaster} months faster
                                    </p>
                                </div>
                            )}
                            {lifetimeSavings > 0 && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Lifetime Savings</p>
                                    <p className="text-xl font-bold text-green-600">
                                        $<AnimatedNumber value={lifetimeSavings} decimals={2} />
                                    </p>
                                </div>
                            )}
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Upfront Cost</p>
                                <p className="text-xl font-bold">
                                    $<AnimatedNumber value={totalUpfrontCost} decimals={2} />
                                </p>
                            </div>
                            {breakEvenMonths !== null && breakEvenMonths > 0 && (
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">Break Even Point</p>
                                    <p className="text-xl font-bold">
                                        {breakEvenMonths} months
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Current Loan Info */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Current Loan (Remaining)</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Remaining Balance:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={existingLoanRemaining} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Monthly Payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={existingMonthlyPayment} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Remaining Months:</span>
                                <span className="text-sm font-semibold">{remainingMonthsExisting} months</span>
                            </div>
                        </div>
                    </div>

                    {/* New Loan Info */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">New Loan</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Loan Amount:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={newLoanAmount} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Monthly Payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={newMonthlyPayment} decimals={2} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Loan Term:</span>
                                <span className="text-sm font-semibold">{debouncedNewLoanTerm * 12} months</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Comparison Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="bg-card border border-border rounded-lg p-6 overflow-x-auto"
            >
                <h2 className="text-2xl font-bold mb-4">Comparison Table</h2>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left p-3 font-semibold">Metric</th>
                                <th className="text-right p-3 font-semibold">Current Loan (Remaining)</th>
                                <th className="text-right p-3 font-semibold">New Loan</th>
                                <th className="text-right p-3 font-semibold">Difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Principal/Loan Amount</td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={existingLoanRemaining} decimals={2} />
                                </td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={newLoanAmount} decimals={2} />
                                </td>
                                <td className={`p-3 text-right font-semibold ${newLoanAmount - existingLoanRemaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${(newLoanAmount - existingLoanRemaining).toFixed(2)}
                                </td>
                            </tr>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Monthly Payment</td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={existingMonthlyPayment} decimals={2} />
                                </td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={newMonthlyPayment} decimals={2} />
                                </td>
                                <td className={`p-3 text-right font-semibold ${monthlySavings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${monthlySavings >= 0 ? '-' : ''}<AnimatedNumber value={Math.abs(monthlySavings)} decimals={2} />
                                </td>
                            </tr>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Length</td>
                                <td className="p-3 text-right font-semibold">{remainingMonthsExisting} months</td>
                                <td className="p-3 text-right font-semibold">{debouncedNewLoanTerm * 12} months</td>
                                <td className={`p-3 text-right font-semibold ${monthsFaster >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {monthsFaster >= 0 ? '-' : ''}{Math.abs(monthsFaster)} months
                                </td>
                            </tr>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Interest Rate/APR</td>
                                <td className="p-3 text-right font-semibold">
                                    <AnimatedNumber value={debouncedExistingInterestRate} decimals={3} />%
                                </td>
                                <td className="p-3 text-right font-semibold">
                                    <AnimatedNumber value={newLoanAPR} decimals={3} />%
                                </td>
                                <td className={`p-3 text-right font-semibold ${(debouncedExistingInterestRate - newLoanAPR) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {(debouncedExistingInterestRate - newLoanAPR) >= 0 ? '-' : ''}<AnimatedNumber value={Math.abs(debouncedExistingInterestRate - newLoanAPR)} decimals={3} />%
                                </td>
                            </tr>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Total Monthly Payments</td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={existingTotalMonthlyPayments} decimals={2} />
                                </td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={newTotalMonthlyPayments} decimals={2} />
                                </td>
                                <td className={`p-3 text-right font-semibold ${(existingTotalMonthlyPayments - newTotalMonthlyPayments) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${(existingTotalMonthlyPayments - newTotalMonthlyPayments) >= 0 ? '-' : ''}<AnimatedNumber value={Math.abs(existingTotalMonthlyPayments - newTotalMonthlyPayments)} decimals={2} />
                                </td>
                            </tr>
                            <tr className="border-b border-border">
                                <td className="p-3 text-muted-foreground">Total Interest</td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={existingLoanTotalInterest} decimals={2} />
                                </td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={newLoanTotalInterest} decimals={2} />
                                </td>
                                <td className={`p-3 text-right font-semibold ${(existingLoanTotalInterest - newLoanTotalInterest) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${(existingLoanTotalInterest - newLoanTotalInterest) >= 0 ? '-' : ''}<AnimatedNumber value={Math.abs(existingLoanTotalInterest - newLoanTotalInterest)} decimals={2} />
                                </td>
                            </tr>
                            <tr>
                                <td className="p-3 text-muted-foreground">Cost + Points (Upfront)</td>
                                <td className="p-3 text-right font-semibold">$0</td>
                                <td className="p-3 text-right font-semibold">
                                    $<AnimatedNumber value={totalUpfrontCost} decimals={2} />
                                </td>
                                <td className="p-3 text-right font-semibold">-</td>
                            </tr>
                            <tr>
                                <td className="p-3 text-muted-foreground">Time to Recover Cost/Points</td>
                                <td className="p-3 text-right font-semibold">NA</td>
                                <td className="p-3 text-right font-semibold">
                                    {breakEvenMonths !== null ? `${breakEvenMonths} months` : 'N/A'}
                                </td>
                                <td className="p-3 text-right font-semibold">-</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </motion.div>
        </motion.div>
    )
}

